import {
  buffers, eventChannel,
} from 'redux-saga';
import {
  call, delay,
  put, race, take, takeEvery, takeLatest,
} from 'redux-saga/effects';
import {
  type CanceledOptimizedTerritoriesEventData,
  type FailedOptimizedTerritoriesEventData,
  type OptimizedTerritoriesEventData, type OptimizedTerritoriesStatusEventData,
  registerOptimizedTerritoriesBroadcastListener,
} from '~/_shared/utils/api/broadcast.helpers';
import { noop } from '~/_shared/utils/function.helpers';
import { select } from '~/_shared/utils/saga/effects';
import type { PickAction } from '~/_shared/utils/types/action.type';
import { AppErrorType } from '~/appError/appErrorType.enum';
import {
  BoundaryCreateOptimizedStatusResponse, getMapData, type MapInfoResponse,
} from '~/map/map.repository';
import { MapPageSplitScreenTab } from '~/map/page/splitScreen/mapPageSplitScreen.types';
import type { BoundaryGroupId } from '~/store/boundaries/boundaryIdentifier.type';
import { boundaryGroupsRequest } from '~/store/boundaryGroups/boundaryGroups.actionCreators';
import { boundaryGroupsIsLoadingSelector } from '~/store/boundaryGroups/boundaryGroups.selectors';
import { boundaryTerritoryDetailsIsLoadingSelector } from '~/store/boundaryTerritoryDetails/boundaryTerritoryDetails.selectors';
import { boundaryTerritoryGroupsFetchRequest } from '~/store/boundaryTerritoryGroups/boundaryTerritoryGroups.actionCreators';
import { boundaryTerritoryGroupsIsLoadingSelector } from '~/store/boundaryTerritoryGroups/boundaryTerritoryGroups.selectors';
import type { BoundaryCreateOptimizedAction } from '~/store/frontendState/mapTools/boundary/boundaryCreateOptimized/boundaryCreateOptimized.action';
import { BOUNDARY_CREATE_OPTIMIZED_ADD_ITEM } from '~/store/frontendState/mapTools/boundary/boundaryCreateOptimized/boundaryCreateOptimized.actionTypes';
import type { MapIdState } from '~/store/mapId/mapId.state';
import type { MapInfoAction } from '~/store/mapInfo/mapInfo.action';
import { MAP_INFO_FETCH_DATA_SUCCESS } from '~/store/mapInfo/mapInfo.actionTypes';
import { mapSettingsBoundariesSetPrimaryBoundaryGroupId } from '~/store/mapSettings/boundaries/mapSettingsBoundaries.actionCreators';
import { createAppError } from '~/store/modal/modal.actionCreators';
import { clientIdSelector } from '~/store/selectors/useClientIdSelector';
import { mapIdSelector } from '~/store/selectors/useMapIdSelector';
import { setHighlightStatsBoundaryGroup } from '~/store/stats/stats.actionCreators';
import { userSettingsSplitPanelSetTab } from '~/store/userData/settings/userDataSettings.actionCreators';
import { USER_WEB_SOCKET_BROADCAST_INITIALIZED } from '~/store/userData/userData.actionTypes';
import { translate } from '~/translations/Trans';
import {
  boundaryCreateOptimizedAddItem,
  boundaryCreateOptimizedRemoveItem, boundaryCreateOptimizedUpdateItem,
} from './boundaryCreateOptimized.actionCreators';
import { boundaryCreateOptimizedPendingItemsSelector } from './boundaryCreateOptimized.selectors';
import {
  type BoundaryCreateOptimizedItem, BoundaryCreateOptimizedStatus,
} from './boundaryCreateOptimized.state';
import { createBoundaryCreateOptimizedStatusFromResponse } from './boundaryCreateOptimizedStatus.factory';

const POLLING_DELAY = 60_000;

type OptimizedTerritoriesListenerMessage = {
  type: 'change';
  data: OptimizedTerritoriesStatusEventData;
} | {
  type: 'success';
  data: OptimizedTerritoriesEventData;
} | {
  type: 'error';
  data: FailedOptimizedTerritoriesEventData;
} | {
  type: 'cancel';
  data: CanceledOptimizedTerritoriesEventData;
};

const itemChannelEmitters = new Map<string, Array<(message: OptimizedTerritoriesListenerMessage) => void>>();

const registerOptimizedTerritoriesListener = () => {
  return eventChannel<OptimizedTerritoriesListenerMessage>(emitter => {
    registerOptimizedTerritoriesBroadcastListener({
      onStatusChange: (data) => emitter({ type: 'change', data }),
      onSuccess: (data) => emitter({ type: 'success', data }),
      onError: (data) => emitter({ type: 'error', data }),
      onCancel: (data) => emitter({ type: 'cancel', data }),
    });
    return noop;
  }, buffers.expanding());
};

const registerProgressItemChannel = (item: BoundaryCreateOptimizedItem) => {
  // Create a dedicated channel for this specific item that will receive broadcasted messages
  return eventChannel<OptimizedTerritoriesListenerMessage>((emitterFn) => {
    // Register this emitter to receive messages for this item's hash
    if (!itemChannelEmitters.has(item.hash)) {
      itemChannelEmitters.set(item.hash, []);
    }
    const emitters = itemChannelEmitters.get(item.hash);
    if (emitters) {
      emitters.push(emitterFn);
    }

    return () => {
      // Cleanup: remove this emitter from the registry
      const emitters = itemChannelEmitters.get(item.hash);
      if (emitters) {
        const index = emitters.indexOf(emitterFn);
        if (index > -1) {
          emitters.splice(index, 1);
        }
        // Clean up the map entry if no more emitters for this hash
        if (emitters.length === 0) {
          itemChannelEmitters.delete(item.hash);
        }
      }
    };
  }, buffers.expanding());
};

export function* boundaryCreateOptimizedSagas() {
  yield takeLatest(MAP_INFO_FETCH_DATA_SUCCESS, onMapInfoFetchSuccess);
  yield takeLatest(USER_WEB_SOCKET_BROADCAST_INITIALIZED, initializeWebSocketChannel);
  yield takeEvery(BOUNDARY_CREATE_OPTIMIZED_ADD_ITEM, onOptimizedTerritoryAdded);
}

function* onMapInfoFetchSuccess(action: PickAction<MapInfoAction, typeof MAP_INFO_FETCH_DATA_SUCCESS>) {
  for (const territoryGeneration of action.payload.data.territoryGeneration.filter(item => item.type === 'Maptive')) {
    if (territoryGeneration.status !== BoundaryCreateOptimizedStatus.Finished) {
      yield put(boundaryCreateOptimizedAddItem(territoryGeneration));
    }
  }
}

function* initializeWebSocketChannel() {
  const sharedChannel = registerOptimizedTerritoriesListener();

  try {
    // Start the broadcaster saga that will distribute messages to all item channels
    yield call(broadcastMessagesToItemChannels, sharedChannel);
  }
  finally {
    // Clean up the shared channel when the saga ends or is cancelled
    if (sharedChannel) {
      sharedChannel.close();
    }
  }
}

// Broadcaster saga: reads from sharedChannel once and distributes to all item-specific channels
function* broadcastMessagesToItemChannels(sharedChannel: ReturnType<typeof registerOptimizedTerritoriesListener>) {
  while (true) {
    if (!sharedChannel) {
      break;
    }

    const message: OptimizedTerritoriesListenerMessage = yield take(sharedChannel);

    // Get the hash from the message
    const hash = message.data?.hash;
    if (!hash) {
      continue;
    }

    // Broadcast to all emitters registered for this hash
    const emitters = itemChannelEmitters.get(hash);
    if (emitters) {
      // Send message to all listeners for this hash
      emitters.forEach(emitter => emitter(message));
    }
  }
}

function* onOptimizedTerritoryAdded(action: PickAction<BoundaryCreateOptimizedAction, typeof BOUNDARY_CREATE_OPTIMIZED_ADD_ITEM>) {
  yield call(watchOptimizationProgressSaga, action.payload.item);
}

function* watchOptimizationProgressSaga(item: BoundaryCreateOptimizedItem) {
  const itemChannel = registerProgressItemChannel(item);

  try {
    while (true) {
      if (!itemChannel) {
        break;
      }

      const { message }: { message: OptimizedTerritoriesListenerMessage | undefined } = yield race({
        message: take(itemChannel),
        timeout: delay(POLLING_DELAY),
      });

      if (message) {
        switch (message.type) {
          case 'change': {
            yield put(boundaryCreateOptimizedUpdateItem({
              type: 'Maptive',
              hash: message.data.hash,
              status: createBoundaryCreateOptimizedStatusFromResponse(message.data.status),
              isFailed: false,
            }));
            continue;
          }
          case 'success': {
            yield put(boundaryCreateOptimizedRemoveItem(message.data.hash));
            yield call(handleOptimizedTerritoriesCreateSuccess, {
              startedOnCurrentTab: item.startedOnCurrentTab,
              boundaryGroupId: message.data.btg.boundary_group_id,
            });
            break;
          }
          case 'cancel': {
            yield put(boundaryCreateOptimizedRemoveItem(message.data.hash));
            break;
          }
          case 'error': {
            yield put(boundaryCreateOptimizedRemoveItem(message.data.hash));
            yield put(createAppError({
              type: AppErrorType.General,
              title: translate('Failed to create optimised territories'),
            }));
            break;
          }
          default:
        }
      }
      else {
        const clientId: number | null = yield select<number | null>(clientIdSelector);
        const mapId: MapIdState = yield select<MapIdState>(mapIdSelector);

        if (mapId === null || clientId === null) {
          break;
        }

        const response: { data: MapInfoResponse } = yield call(getMapData, clientId, mapId);
        const mapInfoTerritoryItem = (response.data.territories_generation ?? [])
          .filter(territory => territory.type === 'Maptive')
          .find(territory => territory.hash === item.hash);

        const boundaryCreateOptimizedPendingItems: ReadonlyArray<BoundaryCreateOptimizedItem>
          = yield select<ReadonlyArray<BoundaryCreateOptimizedItem>>(boundaryCreateOptimizedPendingItemsSelector);

        const pendingItem = boundaryCreateOptimizedPendingItems.find(pendingItem => pendingItem.hash === item.hash);

        if (!pendingItem) {
          break;
        }

        if (!mapInfoTerritoryItem || mapInfoTerritoryItem.status === BoundaryCreateOptimizedStatusResponse.Finished) {
          yield put(boundaryCreateOptimizedRemoveItem(item.hash));
          yield call(handleOptimizedTerritoriesCreateSuccess, {
            startedOnCurrentTab: item.startedOnCurrentTab,
          });
          break;
        }
        else {
          yield put(boundaryCreateOptimizedUpdateItem({
            ...item,
            status: createBoundaryCreateOptimizedStatusFromResponse(mapInfoTerritoryItem.status),
          }));
        }
      }
    }
  }
  finally {
    // Clean up the item-specific channel when the saga ends
    if (itemChannel) {
      itemChannel.close();
    }
  }
}

export function* handleOptimizedTerritoriesCreateSuccess({ startedOnCurrentTab, boundaryGroupId }: { startedOnCurrentTab: boolean; boundaryGroupId?: BoundaryGroupId }) {
  const mapId: number | null = yield select<number | null>(mapIdSelector);

  if (mapId !== null) {
    yield put(boundaryTerritoryGroupsFetchRequest(mapId));
    yield put(boundaryGroupsRequest());

    if (startedOnCurrentTab && boundaryGroupId) { // prevent map settings update conflicts when map is opened multiple times
      yield put(mapSettingsBoundariesSetPrimaryBoundaryGroupId(boundaryGroupId));
      yield call(scrollToBoundaryStatisticsChartItem, mapId, boundaryGroupId);
    }
  }
}

export function* scrollToBoundaryStatisticsChartItem(mapId: number, boundaryGroupId: number) {
  const clientId: number | null = yield select(clientIdSelector);

  if (!clientId) {
    return;
  }

  while (true) {
    const areBTGsLoading: boolean = yield select(boundaryTerritoryGroupsIsLoadingSelector);
    const areBoundaryGroupsLoading: boolean = yield select(boundaryGroupsIsLoadingSelector);
    const areBTGDetailsLoading: boolean = yield select(boundaryTerritoryDetailsIsLoadingSelector);
    if (!areBTGsLoading && !areBTGDetailsLoading && !areBoundaryGroupsLoading) {
      break;
    }
    yield delay(500);
  }

  yield put(userSettingsSplitPanelSetTab(clientId, mapId, MapPageSplitScreenTab.Chart));
  yield put(setHighlightStatsBoundaryGroup(boundaryGroupId));
}
